// Copyright (c) Ping. All rights reserved.

import React, { useCallback } from 'react';
import { Pressable, Text, View } from 'react-native';
import { MoreHorizontal, X } from 'lucide-react-native';
// Class-aware scheme (data-theme/.dark) — NEVER react-native's OS-level
// useColorScheme (renders the wrong palette on reload). See repo CLAUDE.md.
import { useColorScheme } from '../foundations/colorScheme';
import { Avatar } from '../components/Avatar';
import { MessagePreview } from '../chat/MessagePreview';
import { createTheme } from '../design-system/theme';
import { threadsStrings } from './strings';
import type { ThreadParentPreview } from './types';

export interface ThreadHeaderProps {
  readonly parent: ThreadParentPreview;
  readonly replyCount: number;
  readonly onClose: () => void;
  readonly onShowOptions?: (() => void) | undefined;
  readonly testID?: string;
}

/**
 * Right-sidebar header inside the open thread panel. Renders the parent
 * message preview, reply-count subtitle, and close affordance. Mirrors
 * `Frame 1000004854` from the Figma `Thread open` frame (`2804:105962`).
 */
export const ThreadHeader = React.memo<ThreadHeaderProps>(
  ({ parent, replyCount, onClose, onShowOptions, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const theme = createTheme(colorScheme);

    const handleClose = useCallback(() => onClose(), [onClose]);
    const handleOptions = useCallback(() => {
      onShowOptions?.();
    }, [onShowOptions]);

    const subtitle = threadsStrings.ping_threads_panel_subtitle(replyCount);
    const previewText = parent.deleted
      ? threadsStrings.ping_threads_parent_deleted
      : parent.previewText;
    const iconColor = scheme === 'dark' ? '#B5BAC3' : theme.colors.text.secondary;

    return (
      <View
        className="gap-3 border-b border-light-border-secondary px-4 py-3 dark:border-[#222429]"
        testID={testID}
      >
        <View className="flex-row items-center justify-between">
          <Text className="font-sans text-[20px] font-bold leading-[26px] text-light-text-primary dark:text-[#F5F6F8]">
            {threadsStrings.ping_threads_panel_title}
          </Text>
          <View className="flex-row items-center gap-2">
            {onShowOptions !== undefined && (
              <Pressable
                onPress={handleOptions}
                accessibilityRole="button"
                accessibilityLabel={threadsStrings.ping_threads_panel_options}
                hitSlop={8}
                className="h-8 w-8 items-center justify-center rounded-full bg-light-surface-secondary active:opacity-70 dark:bg-[#222429]"
              >
                <MoreHorizontal size={18} strokeWidth={2} color={iconColor} />
              </Pressable>
            )}
            <Pressable
              onPress={handleClose}
              accessibilityRole="button"
              accessibilityLabel={threadsStrings.ping_threads_panel_close}
              hitSlop={8}
              className="h-8 w-8 items-center justify-center rounded-full bg-light-surface-secondary active:opacity-70 dark:bg-[#222429]"
            >
              <X size={18} strokeWidth={2} color={iconColor} />
            </Pressable>
          </View>
        </View>
        <View className="flex-row items-start gap-3">
          <Avatar
            size="small"
            initials={parent.authorName}
            {...(parent.authorImageUri !== undefined && parent.authorImageUri !== null
              ? { imageUri: parent.authorImageUri }
              : {})}
          />
          <View className="min-w-0 flex-1 gap-0.5">
            <View className="flex-row items-baseline gap-2">
              <Text
                className="flex-shrink font-sans text-[14px] font-semibold text-light-text-primary dark:text-[#F5F6F8]"
                numberOfLines={1}
              >
                {parent.authorName}
              </Text>
              <Text className="font-sans text-[11px] font-medium text-light-text-tertiary dark:text-[#6B7280]">
                {formatHourMinute(parent.timestamp)}
              </Text>
            </View>
            {parent.deleted ? (
              <Text
                className="font-sans text-[14px] italic leading-5 text-light-text-tertiary dark:text-[#6B7280]"
                numberOfLines={2}
              >
                {previewText}
              </Text>
            ) : (
              // Shared conversation-preview renderer: turns the formatter's
              // emoji glyph (📷 / 📞 / 📊 …) into the proper 12×12 kind icon +
              // one-line text, matching the conversation list.
              <MessagePreview
                preview={parent.previewText}
                className="text-light-text-primary dark:text-[#E8EAED]"
              />
            )}
          </View>
        </View>
        <Text className="font-sans text-[12px] font-medium text-light-text-tertiary dark:text-[#6B7280]">
          {subtitle}
        </Text>
      </View>
    );
  },
);

ThreadHeader.displayName = 'ThreadHeader';

function formatHourMinute(timestamp: number): string {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
